import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../utils/axios";

interface AdminProfile {
  id: number;
  username: string;
  email: string;
  role: string;
}

const AdminAccount = () => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(false);

  const [password, setPassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  /* ================= FETCH PROFILE ================= */
  const fetchProfile = async () => {
    try {
      const res = await api.get(
        "/profile",
      );
      setProfile(res.data.data);
    } catch (err) {
      console.error("Failed to load admin profile");
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  /* ================= PASSWORD CHANGE ================= */
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized");
      return;
    }

    const { oldPassword, newPassword, confirmPassword } = password;

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const res = await api.post(
        "/changepassword",
        { token, oldPassword, newPassword, confirmPassword }
      );

      toast.success(res.data.message || "Password updated");
      handleLogout();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || "Password update failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handelRestoreDatabase = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized");
      return;
    }

    setLoading(true)

    try {
      const res = await api.post(
        "/restoreDatabase",
      );

      setLoading(false);
      if(res.data.status){
        toast.success(res.data.message || "updated");
      }
     handleLogout();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || "Password update failed"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div className=" mx-auto space-y-8">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Admin Account
        </h1>
        <p className="text-sm text-gray-500">
          Manage your admin profile, security, and session
        </p>

        <div className="md:col-span-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={(e)=> handelRestoreDatabase(e)}
              disabled={loading}
              className="px-6 py-2 rounded-lg bg-gray-900 text-white disabled:opacity-50"
            >
              {loading ? "Restoring..." : "Restore"}
            </button>
          </div>
      </div>

      {/* ================= PROFILE CARD ================= */}
      <div className="bg-white border rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          Profile Information
        </h2>

        {profile ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Admin Name</p>
              <p className="font-medium">{profile.username}</p>
            </div>

            <div>
              <p className="text-gray-500">Email</p>
              <p className="font-medium">{profile.email}</p>
            </div>

            <div>
              <p className="text-gray-500">Role</p>
              <p className="font-medium capitalize">{profile.role}</p>
            </div>

            <div>
              <p className="text-gray-500">Admin ID</p>
              <p className="font-medium">#{profile.id}</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">Loading profile...</p>
        )}
      </div>

      {/* ================= SECURITY ================= */}
      <div className="bg-white border rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          Security Settings
        </h2>

        <form
          onSubmit={handlePasswordChange}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <input
            type="password"
            placeholder="Current password"
            className="border rounded-lg px-4 py-2"
            onChange={(e) =>
              setPassword({ ...password, oldPassword: e.target.value })
            }
          />

          <input
            type="password"
            placeholder="New password"
            className="border rounded-lg px-4 py-2"
            onChange={(e) =>
              setPassword({ ...password, newPassword: e.target.value })
            }
          />

          <input
            type="password"
            placeholder="Confirm new password"
            className="border rounded-lg px-4 py-2"
            onChange={(e) =>
              setPassword({
                ...password,
                confirmPassword: e.target.value,
              })
            }
          />

          <div className="md:col-span-2 flex justify-end gap-3">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 rounded-lg bg-gray-900 text-white disabled:opacity-50"
            >
              {loading ? "Updating..." : "Change Password"}
            </button>
          </div>
        </form>
      </div>

      {/* ================= ACTIONS ================= */}
      <div className="flex justify-end">
        <button
          onClick={handleLogout}
          className="px-6 py-2 rounded-lg border text-red-600 hover:bg-red-50"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default AdminAccount;
